import { useRef, useState } from "react";
import { Paperclip, X, ExternalLink } from "lucide-react";
import {
  useSimulationAttachments,
  useUploadSimulationAttachment,
  useDeleteSimulationAttachment,
} from "@/features/finances/hooks/useSimulationDiscussion";

interface Props {
  simulationId: string;
}

export function SimulationAttachments({ simulationId }: Props) {
  const { data: attachments = [] } = useSimulationAttachments(simulationId);
  const upload = useUploadSimulationAttachment(simulationId);
  const del = useDeleteSimulationAttachment(simulationId);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  async function handleFiles(files: FileList) {
    setUploading(true);
    try {
      // wieloplikowo — każdy plik osobnym żądaniem (jeden ManagedFile na plik)
      for (const file of Array.from(files)) {
        await upload.mutateAsync(file);
      }
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5 print:hidden">
      <div className="mb-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <Paperclip size={16} className="text-[var(--color-primary)]" /> Załączniki
        </h3>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 pl-6">
          Wspólne dla całej symulacji — niezależne od wybranego wariantu.
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        {attachments.map((att) => (
          <div
            key={att.id}
            className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-lg px-3 py-2 group"
          >
            <a
              href={att.url ?? "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center gap-1.5 text-sm text-[var(--color-primary)] hover:underline truncate"
            >
              <ExternalLink size={12} className="flex-shrink-0" />
              <span className="truncate">{att.original_name}</span>
            </a>
            {att.uploaded_by_name && (
              <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                {att.uploaded_by_name}
              </span>
            )}
            <button
              onClick={() => del.mutate(att.id)}
              className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
              title="Usuń załącznik"
            >
              <X size={13} />
            </button>
          </div>
        ))}

        {attachments.length === 0 && (
          <p className="text-sm text-gray-400 dark:text-gray-500 italic">Brak załączników.</p>
        )}

        <input
          ref={fileRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files.length) handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="mt-1 flex items-center gap-1.5 text-sm text-[var(--color-primary)] border border-[var(--color-primary)] border-dashed rounded-lg px-3 py-2 hover:bg-[var(--color-primary-dim)] transition-colors w-fit disabled:opacity-50"
        >
          <Paperclip size={13} />
          {uploading ? "Przesyłanie…" : "Dodaj pliki"}
        </button>
      </div>
    </div>
  );
}
